import React from 'react';
import { Helmet } from 'react-helmet-async';
import { useNavigate } from 'react-router-dom';
import { LegalPageLayout } from '../components/LegalPageLayout';
import { SiteHeader } from '../components/SiteHeader';
import { AppFooter } from '../components/AppFooter';
import { useScreenInit } from '../useScreenInit';
export function Terms() {
  useScreenInit('Terms');
  const navigate = useNavigate();
  return <div className="min-h-screen bg-white flex flex-col" data-id="element-1468">
      <Helmet data-id="element-1469">
        <title data-id="element-1470">Terms of Service | Affordable Medical Tourism</title>
        <meta name="description" content="Terms of Service for affordablemedicaltourism.com, a product of Kosansh Solutions Inc." data-id="element-1471" />
      </Helmet>

      <SiteHeader brand="Affordable Medical Tourism" links={[{
      label: 'Procedures',
      href: '/procedures'
    }, {
      label: 'How it works',
      href: '/how-it-works'
    }, {
      label: 'Destinations',
      href: '/destinations'
    }, {
      label: 'FAQ',
      href: '/faq'
    }]} contactCtaLabel="Get a free quote" onContactCta={() => navigate('/contact')} data-id="element-1472" />

      <main className="flex-1" data-id="element-1473">
        <LegalPageLayout title="Terms of Service" lastUpdated="October 15, 2023" illustrative={true} intro={<p className="text-lg text-slate-600 mb-8" data-id="element-1475">
              Welcome to affordablemedicaltourism.com, a product of Kosansh
              Solutions Inc. ("Company," "we," "us," or "our"). These Terms of
              Service ("Terms") govern your access to and use of our website,
              services, and applications (collectively, the "Services"). By
              accessing or using the Services, you agree to be bound by these
              Terms. If you do not agree to these Terms, please do not use our
              Services.
            </p>} sections={[{
        id: 'acceptance',
        heading: '1. Acceptance of Terms',
        body: <div className="space-y-4 text-slate-600" data-id="element-1476">
                  <p data-id="element-1477">
                    By accessing or using affordablemedicaltourism.com, you
                    confirm that you can form a binding contract with Kosansh
                    Solutions Inc., that you accept these Terms and that you
                    agree to comply with them. Your access to and use of our
                    Services is also subject to our Privacy Policy and Medical
                    Disclaimer, which are incorporated herein by reference.
                  </p>
                  <p data-id="element-1478">
                    We reserve the right to modify these Terms at any time. We
                    will provide notice of significant changes by updating the
                    "Last Updated" date at the top of this page. Your continued
                    use of the Services following the posting of revised Terms
                    means that you accept and agree to the changes.
                  </p>
                </div>
      }, {
        id: 'services-description',
        heading: '2. Description of Services',
        body: <div className="space-y-4 text-slate-600" data-id="element-1479">
                  <p data-id="element-1480">
                    affordablemedicaltourism.com operates as a referral and
                    coordination platform. We connect individuals seeking
                    medical, dental, or cosmetic procedures with internationally
                    accredited healthcare facilities and providers outside their
                    home country.
                  </p>
                  <p data-id="element-1481">
                    <strong data-id="element-1482">
                      Crucially, Kosansh Solutions Inc. is not a healthcare
                      provider.
                    </strong>{' '}
                    We do not provide medical advice, diagnosis, or treatment.
                    Our role is strictly limited to providing information,
                    facilitating communication, and assisting with travel and
                    logistical coordination. The ultimate decision to undergo
                    any medical procedure, and the selection of a healthcare
                    provider, rests entirely with you.
                  </p>
                </div>
      }, {
        id: 'user-responsibilities',
        heading: '3. User Responsibilities and Representations',
        body: <div className="space-y-4 text-slate-600" data-id="element-1483">
                  <p data-id="element-1484">By using our Services, you represent and warrant that:</p>
                  <ul className="list-disc pl-6 space-y-2" data-id="element-1485">
                    <li data-id="element-1486">
                      You are at least 18 years of age and have the legal
                      capacity to enter into these Terms.
                    </li>
                    <li data-id="element-1487">
                      All information you provide to us, including medical
                      history and contact details submitted via our contact
                      forms, is accurate, current, and complete.
                    </li>
                    <li data-id="element-1488">
                      You will consult with your primary care physician or a
                      qualified healthcare professional in your home country
                      before making any decisions regarding medical travel or
                      procedures.
                    </li>
                    <li data-id="element-1489">
                      You understand that all communications with Kosansh
                      Solutions Inc. must occur through our designated contact
                      forms, and we do not conduct official business via direct
                      email links on our public site to ensure data security.
                    </li>
                  </ul>
                </div>
      }, {
        id: 'limitation-liability',
        heading: '4. Limitation of Liability',
        body: <div className="space-y-4 text-slate-600" data-id="element-1490">
                  <p data-id="element-1491">
                    To the fullest extent permitted by applicable law, Kosansh
                    Solutions Inc., its officers, directors, employees, and
                    agents shall not be liable for any indirect, incidental,
                    special, consequential, or punitive damages, or any loss of
                    profits or revenues, whether incurred directly or
                    indirectly, or any loss of data, use, goodwill, or other
                    intangible losses, resulting from:
                  </p>
                  <ul className="list-disc pl-6 space-y-2" data-id="element-1492">
                    <li data-id="element-1493">
                      Your access to or use of or inability to access or use the
                      Services.
                    </li>
                    <li data-id="element-1494">
                      Any conduct or content of any third party on the Services,
                      including without limitation, any defamatory, offensive,
                      or illegal conduct of other users or third parties.
                    </li>
                    <li data-id="element-1495">
                      Any medical outcomes, complications, or malpractice
                      arising from procedures performed by third-party
                      healthcare providers recommended or facilitated through
                      our platform.
                    </li>
                  </ul>
                  <p data-id="element-1496">
                    Our total liability for any claims arising under these Terms
                    shall be limited to the amount you paid us to use the
                    Services, if any.
                  </p>
                </div>
      }, {
        id: 'contact-info',
        heading: '5. Contact Information',
        body: <div className="space-y-4 text-slate-600" data-id="element-1497">
                  <p data-id="element-1498">
                    affordablemedicaltourism.com is a product of Kosansh
                    Solutions Inc., based in Fort Worth, TX.
                  </p>
                  <p data-id="element-1499">
                    For any questions regarding these Terms, please contact us
                    via our official contact form. You may also reach us by
                    phone at 857-567-2674. Please note that for security and
                    privacy reasons, we do not publish direct email addresses on
                    our website.
                  </p>
                </div>
      }]} data-id="element-1474" />
      </main>

      <AppFooter data-id="element-1500" />
    </div>;
}